import { useQuery, UseQueryOptions, QueryKey } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

/**
 * Debounce utility for promises
 */
function useDebouncedQueryFn<T>(queryFn: () => Promise<T>, delay: number) {
  const timeout = useRef<NodeJS.Timeout | null>(null);

  return () => {
    if (timeout.current) clearTimeout(timeout.current);
    return new Promise<T>((resolve, reject) => {
      timeout.current = setTimeout(() => {
        queryFn().then(resolve).catch(reject);
      }, delay);
    });
  };
}

/** Local options type excluding fields we set internally */
type AppQueryOptions<T> = Omit<UseQueryOptions<T, Error, T, QueryKey>, 'queryKey' | 'queryFn'> & {
  debounceMs?: number;
};

/**
 * Generic wrapper for TanStack Query useQuery hook with debounce and cache.
 */
export function useAppQuery<T>(
  queryKey: QueryKey,
  queryFn: () => Promise<T>,
  options?: AppQueryOptions<T>
) {
  const queryClient = useQueryClient();
  // Separate debounceMs from react-query options
  const { debounceMs, ...queryOptions } = options || {};
  // Always call the hook, but use debounceMs to decide which function to use
  const debouncedQuery = useDebouncedQueryFn(queryFn, debounceMs ?? 300);
  const queryFunction = debounceMs ? debouncedQuery : queryFn;

  const {
    data,
    error,
    isLoading,
    isError,
    isSuccess,
    refetch,
    ...rest
  } = useQuery<T, Error, T, QueryKey>({
    queryKey,
    queryFn: queryFunction,
    // Prevent unnecessary retries for now (can be overridden)
    retry: queryOptions.retry ?? false,
    // Avoid refetch on focus unless explicitly enabled
    refetchOnWindowFocus: queryOptions.refetchOnWindowFocus ?? false,
    // Do not refetch on mount if data is fresh
    refetchOnMount: queryOptions.refetchOnMount ?? false,
    // Avoid auto refetch on reconnect unless asked
    refetchOnReconnect: queryOptions.refetchOnReconnect ?? false,
    // Keep previous data during pagination / key changes
    // Cache & staleness settings: default 5 min fresh, 15 min retained in cache
    staleTime: queryOptions.staleTime ?? 5 * 60 * 1000,
    gcTime: queryOptions.gcTime ?? 15 * 60 * 1000,
    // Spread remaining valid options last
    ...queryOptions,
  });

  return {
    data,
    error,
    isLoading,
    isError,
    isSuccess,
    refetch,
    queryClient,
    ...rest,
  };
}